import React from 'react';
import { Link } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Grid,
  Stack,
  Typography,
} from '@mui/material';
import PolicyOutlinedIcon from '@mui/icons-material/PolicyOutlined';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import ChecklistOutlinedIcon from '@mui/icons-material/ChecklistOutlined';
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import {
  accentCardSx,
  darkPanelSx,
  eyebrowSx,
  heroPanelSx,
  iconBadgeSx,
  pageHeadlineSx,
  subtleCopySx,
  warmPanelSx,
  CheckGlyph,
  checkBulletSx,
} from './designSystem';

const platformSections = [
  {
    icon: <PolicyOutlinedIcon />,
    accent: '#13947F',
    tint: 'rgba(19,148,127,0.12)',
    title: 'District baseline',
    body: 'One default policy that applies to every enrolled device. Set the universal standard once — everything else layers on top of it.',
  },
  {
    icon: <GroupsOutlinedIcon />,
    accent: '#46729B',
    tint: 'rgba(70,114,155,0.12)',
    title: 'Profiles for real environments',
    body: 'Classrooms, labs, libraries, and testing windows each get their own layer of exceptions without touching the baseline.',
  },
  {
    icon: <FactCheckOutlinedIcon />,
    accent: '#C08A2D',
    tint: 'rgba(192,138,45,0.12)',
    title: 'Review rules for edge cases',
    body: 'Write plain-language rules for the gray-area content that category lists never catch — the AI applies them to every page.',
  },
  {
    icon: <ChecklistOutlinedIcon />,
    accent: '#0B2E43',
    tint: 'rgba(11,46,67,0.08)',
    title: 'Quick controls and hard overrides',
    body: 'Temporary quick allows for the lesson that just got blocked, plus permanent allowlists and blocklists for the certain calls.',
  },
];

const useCases = [
  'Elementary devices with cautious defaults',
  'Secondary research that needs open-but-safe access',
  'Locked-down testing windows',
  'Libraries, labs, and shared carts',
];

function InfoPage() {
  return (
    <Container sx={{ py: { xs: 6, md: 8 } }}>
      <Stack spacing={5}>
        <Box sx={{ ...heroPanelSx, p: { xs: 3.5, md: 6 } }}>
          <Typography variant="overline" sx={eyebrowSx}>
            Platform Overview
          </Typography>
          <Typography variant="h2" sx={{ ...pageHeadlineSx, mt: 1.5, maxWidth: 760 }}>
            Built for the pages school teams actually struggle to govern.
          </Typography>
          <Typography sx={{ ...subtleCopySx, mt: 2, maxWidth: 640, fontSize: '1.05rem' }}>
            Four layers work together: a district baseline, environment profiles, plain-language review rules, and fast manual overrides.
          </Typography>
        </Box>

        <Grid container spacing={3}>
          {platformSections.map((section) => (
            <Grid item xs={12} md={6} key={section.title}>
              <Card sx={{ ...accentCardSx(section.accent), height: '100%' }}>
                <CardContent sx={{ p: { xs: 3, md: 3.5 } }}>
                  <Box sx={{ ...iconBadgeSx(section.tint, section.accent), mb: 2 }}>
                    {section.icon}
                  </Box>
                  <Typography variant="h5" sx={{ color: '#0B2E43', mb: 1.25 }}>
                    {section.title}
                  </Typography>
                  <Typography sx={subtleCopySx}>{section.body}</Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        <Card sx={darkPanelSx}>
          <CardContent sx={{ p: { xs: 3, md: 5 } }}>
            <Typography variant="h3" sx={{ mb: 3, fontSize: { xs: '1.6rem', md: '2rem' } }}>
              Where it fits best
            </Typography>
            <Grid container spacing={2.5}>
              {useCases.map((item) => (
                <Grid item xs={12} md={6} key={item}>
                  <Stack direction="row" spacing={1.5} alignItems="flex-start">
                    <Box sx={{ ...checkBulletSx, bgcolor: 'rgba(19,148,127,0.3)', border: '1px solid rgba(19,148,127,0.5)' }}>
                      <CheckGlyph color="#7EE8D6" />
                    </Box>
                    <Typography sx={{ color: '#D5E2E7', lineHeight: 1.7 }}>{item}</Typography>
                  </Stack>
                </Grid>
              ))}
            </Grid>
          </CardContent>
        </Card>

        <Card sx={warmPanelSx}>
          <CardContent sx={{ p: { xs: 3, md: 4 } }}>
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              spacing={2.5}
              alignItems={{ xs: 'flex-start', md: 'center' }}
              justifyContent="space-between"
            >
              <Stack direction="row" spacing={2} alignItems="flex-start">
                <Box sx={iconBadgeSx('rgba(192,138,45,0.16)', '#A8771F')}>
                  <AutoAwesomeOutlinedIcon />
                </Box>
                <Box>
                  <Typography variant="h4" sx={{ color: '#0B2E43', mb: 0.75 }}>
                    The AI layer
                  </Typography>
                  <Typography sx={{ ...subtleCopySx, maxWidth: 560 }}>
                    Use it for the gray areas where whole-site blocking is too blunt — it reads the page itself and applies your written policy, one page at a time.
                  </Typography>
                </Box>
              </Stack>
              <Button
                variant="contained"
                color="primary"
                component={Link}
                to="/demo"
                endIcon={<ArrowForwardRoundedIcon />}
                sx={{ flexShrink: 0 }}
              >
                See It Decide Live
              </Button>
            </Stack>
          </CardContent>
        </Card>
      </Stack>
    </Container>
  );
}

export default InfoPage;
